// src/services/api/returnBatch.js
import { apiClient } from './base';

/**
 * Get today's active return batches for a business courier
 * @param {number} businessCourierId - Business courier account ID
 * @returns {Promise<Object>} List of today's active return batches
 */
export const getTodayReturnBatches = async (businessCourierId) => {
  try {
    const response = await apiClient.get('/return-batches/today', {
      params: { businessCourierId }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new return batch
 * @param {Object} batchData - Batch creation data
 * @param {number} batchData.businessCourierId - Business courier account ID
 * @param {number} batchData.warehouseId - Warehouse ID
 * @param {string} batchData.notes - Optional notes
 * @returns {Promise<Object>} Created batch details
 */
export const createReturnBatch = async (batchData) => {
  try {
    const response = await apiClient.post('/return-batches', batchData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Add order to return batch by scanning
 * @param {number} batchId - Return batch ID
 * @param {Object} scanData - Scan data
 * @param {string} scanData.identifier - Airwaybill number or order number
 * @param {string} scanData.notes - Optional notes
 * @returns {Promise<Object>} Order details and stock movements
 */
export const addOrderToReturnBatch = async (batchId, scanData) => {
  try {
    const response = await apiClient.post(`/return-batches/${batchId}/orders`, scanData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all return batches with filters
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Paginated return batches
 */
export const getAllReturnBatches = async (params = {}) => {
  try {
    const response = await apiClient.get('/return-batches', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get return batch details by ID
 * @param {number} batchId - Return batch ID
 * @returns {Promise<Object>} Batch details with orders
 */
export const getReturnBatchById = async (batchId) => {
  try {
    const response = await apiClient.get(`/return-batches/${batchId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get the aggregated product list for a return batch
 * @param {number} batchId - Return batch ID
 * @returns {Promise<Object>} Products with total quantities and order counts
 */
export const getReturnBatchProductList = async (batchId) => {
  const response = await apiClient.get(`/return-batches/${batchId}/product-list`);
  return response.data;
};

/**
 * Close a return batch
 * @param {number} batchId - Return batch ID
 * @returns {Promise<Object>} Closed batch details
 */
export const closeReturnBatch = async (batchId) => {
  try {
    const response = await apiClient.put(`/return-batches/${batchId}/close`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get return batch statistics
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Statistics data
 */
export const getReturnBatchStatistics = async (params = {}) => {
  try {
    const response = await apiClient.get('/return-batches/statistics/summary', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get user's accessible warehouses for return operations
 * @returns {Promise<Object>} List of accessible warehouses
 */
export const getUserWarehouses = async () => {
  try {
    const response = await apiClient.get('/return-batches/warehouses/accessible');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get return orders dashboard summary
 * @param {Object} params - Query parameters
 * @param {number} params.warehouseId - Optional warehouse filter
 * @param {string} params.dateFrom - Optional start date
 * @param {string} params.dateTo - Optional end date
 * @returns {Promise<Object>} Dashboard summary with today's and this week's returns
 */
export const getDashboardSummary = async (params = {}) => {
  try {
    const response = await apiClient.get('/return-batches/dashboard-summary', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};